import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { acceptInvite, declineInvite } from '@/services/invite';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getFullName, getInitials } from '@/lib/string';
import { useMyInvites } from '@/hooks/use-my-invites';
import { errorHandler } from '@/lib/utils';
export function WorkspaceInvitesList() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: invites = [], isLoading } = useMyInvites();
    const accept = useMutation({
        mutationFn: acceptInvite,
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({ queryKey: ['my-invites'] });
            queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        },
        onError: errorHandler,
    });
    const decline = useMutation({
        mutationFn: declineInvite,
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({ queryKey: ['my-invites'] });
        },
        onError: errorHandler,
    });
    if (isLoading) {
        return <p className="text-sm text-muted-foreground">{t('loading')}</p>;
    }
    if (!invites.length) {
        return <p className="text-sm text-muted-foreground">{t('invite.none')}</p>;
    }
    return (<div className="space-y-3">
            {invites.map((invite) => (<Card key={`invites-${invite.publicId}`}>
                    <CardContent>
                        <div className="flex items-center gap-3">
                            <Avatar>
                                {invite.workspace.logoUrl ? (<AvatarImage src={invite.workspace.logoUrl}/>) : (<AvatarFallback>{getInitials(invite.workspace.name)}</AvatarFallback>)}
                            </Avatar>

                            <div>
                                <p className="text-sm font-medium">{invite.workspace.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {t('invite.invited_as', { role: invite.role })}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {t('invite.by', { name: getFullName(invite.invitedBy) })}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => decline.mutate(invite.publicId)}>
                            {t('decline')}
                        </Button>
                        <Button size="sm" onClick={() => accept.mutate(invite.publicId)}>
                            {t('accept')}
                        </Button>
                    </CardFooter>
                </Card>))}
        </div>);
}
